/**
 * AdminGuard — /admin/* 라우트 보호 컴포넌트 (GDEV-63).
 *
 * 정책:
 *  1. 로딩 중(`isLoading`)이면 스피너 대용 최소 안내만 노출 (비인증 flash 방지).
 *  2. 미인증 → `/` 로 replace navigate. 로그인 모달을 오픈해서 재시도를 유도.
 *     GDEV-157: 현 path 를 ``returnTo`` 로 authModalStore 에 전달 — 로그인
 *     성공 후 LoginModal 이 해당 path 로 복귀 navigate.
 *  3. 인증됐으나 role != 'admin' → 인라인 403 안내 (서비스 라우트로 이탈시키지
 *     않음 — 관리자가 URL 공유받았을 때의 UX 에 이득).
 *  4. admin 이면 children 을 그대로 렌더.
 *
 * BE (`require_role("admin")`) 이 HTTP 403 으로 이중 방어하므로 이 가드는
 * UX 용 1차 필터일 뿐, 보안 경계는 아님.
 */

import { useAuthModalStore } from '@/stores/authModalStore'
import { useAuthStore } from '@/stores/authStore'
import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router'

interface AdminGuardProps {
  children: React.ReactNode
}

export function AdminGuard({ children }: AdminGuardProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isLoading = useAuthStore((s) => s.isLoading)
  const user = useAuthStore((s) => s.user)
  const openAuthModal = useAuthModalStore((s) => s.open)
  const location = useLocation()

  // 세션 복원 중 — Outlet 을 비활성 상태로 가볍게 표시
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const returnTo = `${location.pathname}${location.search}${location.hash}`
      openAuthModal('signin', returnTo)
    }
  }, [isLoading, isAuthenticated, openAuthModal, location.pathname, location.search, location.hash])

  if (isLoading) {
    return (
      <div className="flex min-h-[240px] items-center justify-center text-[12px] text-muted-foreground">
        세션 확인 중…
      </div>
    )
  }

  if (!isAuthenticated) {
    // 비로그인: 홈으로 리다이렉트 (로그인 모달은 effect 가 오픈)
    return <Navigate to="/" replace />
  }

  if (user?.role !== 'admin') {
    return (
      <div
        role="alert"
        className="mx-auto mt-10 max-w-md border border-border-base bg-card p-6 text-center"
      >
        <h2 className="text-[14px] font-semibold text-foreground">권한 없음 (403)</h2>
        <p className="mt-2 text-[12px] text-muted-foreground">
          관리자 권한이 필요한 페이지입니다. 계정 권한을 확인하세요.
        </p>
        <p className="mt-3 text-[11px] text-muted-foreground">
          현재 역할: <span className="mono">{user?.role ?? '(알 수 없음)'}</span>
        </p>
      </div>
    )
  }

  return <>{children}</>
}
